import type { CSSProperties } from 'react';
import { colors } from '../theme';

export interface EnvVarRow {
  key: string;
  value: string;
}

const rowInputStyle: CSSProperties = {
  flex: 1,
  padding: '8px 10px',
  borderRadius: 6,
  border: `1px solid ${colors.border}`,
  fontSize: 13,
  color: colors.text,
  background: colors.surface,
};

/**
 * A generic key/value environment-variable editor, shared across all 5
 * create flows — this is the "wire an API key or a LiteLLM URL through to
 * my app" path (AI Agent's Model Provider field alone had nowhere for a
 * user to actually give a credential). Whatever's entered here rides the
 * same extraEnvVars pipe that already tags MUMZWORLD_APP_TYPE etc. onto the
 * deployed Coolify app — no new backend mechanism needed.
 */
export function EnvVarsEditor({
  rows,
  onChange,
}: {
  rows: EnvVarRow[];
  onChange: (rows: EnvVarRow[]) => void;
}) {
  const updateRow = (index: number, patch: Partial<EnvVarRow>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };
  const addRow = () => {
    onChange([...rows, { key: '', value: '' }]);
  };

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              style={rowInputStyle}
              placeholder="KEY"
              value={row.key}
              onChange={(e) => updateRow(i, { key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
              aria-label={`Environment variable ${i + 1} key`}
            />
            <input
              style={rowInputStyle}
              type="password"
              placeholder="value"
              value={row.value}
              onChange={(e) => updateRow(i, { value: e.target.value })}
              aria-label={`Environment variable ${i + 1} value`}
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              aria-label={`Remove environment variable ${i + 1}`}
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.textMuted,
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
                padding: '4px 8px',
              }}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        style={{
          marginTop: 8,
          border: 'none',
          background: 'transparent',
          color: colors.primary,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          padding: 0,
        }}
      >
        + Add variable
      </button>
    </div>
  );
}
